import React, { useMemo, useEffect, useCallback, useState, useContext, useRef } from 'react';
import { Grid, CircularProgress, FormControl, TextField, MenuItem, Button, Popper } from '@material-ui/core';
import { SwapVert } from '@material-ui/icons';
import { css } from '@emotion/react';
import {
    useCustomerStops,
    useCallbacks,
    useRouteTimeLimits,
    useInvalidEventStates,
    useRouteExceedsWindowCeiling,
    usePreviewOrders,
} from '../hooks';
import { useItemQuantity } from '@/hooks';
import { poundFormatter, integerFormatter, hourMinuteFormatter } from '@/constants/formats';
import StartTimeSelect from '../../misc/StartTimeSelect';
import StopListItem from './StopListItem';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import handleGeocode from '@/utilities/handleGeocode';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import _, { omit } from 'lodash';
import routeStatusOptions from '@/constants/routeStatusOptions';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { useMutation, useApolloClient } from '@apollo/client';
import { useClientUser } from '@/hooks/useClientUser';
import { useBusinessTimezone } from '@/hooks/useBusinessTimezone';
import { genAttributes, validate } from '@onward-delivery/core';
import { PlanningDataContext, PlanningSelectionContext, PlanningUiContext } from '../context';
import { routeTimeFormatter, routeTimeFormatterTZ } from '@/constants/formats';
import { ROUTE_READONLY_FIELDS } from '@onward-delivery/core';
import { MODALS, TEMP_STOP } from '../constants';
import {
    partnerAssignmentUpdates,
    partnerTypeFor,
    routePartner,
    routePartnerOptions,
    selectableResources,
} from '../planningDerivations';
import { useResourcesById } from '../hooks/useResourcesById';
import StopBookend from './StopBookend';
import {
    Body1,
    Body2,
    GridItemRow,
    GridScrollY,
    GrowingGrid,
    PlanningListItem,
    PrimaryButton,
    H1,
    RouteDetailsIconButton,
} from '../blocks';
import { UPDATE_ROUTE_STOPS, UPSERT_ROUTE } from '../graphql/mutations';
import { QUERY_PLANNING_DATA } from '../graphql/queries';
import { buildUpdateStopsOptimistic, updateStopsCacheHandler } from '../graphql/updateStopsOptimistic';
import * as Sentry from '@sentry/react';
import { StrictModeDroppable } from './StrictModeDroppable';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import {
    calcEstimatedRouteTime,
    calculateGoogleTimeframes,
    routeIsConfirmed,
    routeIsMissingTimeframes,
    routeNeedsRecalculating,
} from '../util';
import { getTimezoneOffset } from 'date-fns-tz';
import { convertToISO } from '@/utilities/convertToISO';
import { colors } from '@/styles';
import { post } from '@/utilities/onwardClient';
import { MAPBOX_OPTIMIZE } from '@/constants/apiRoutes';
import { calcDefaultServiceTime, getDispatchSettingPreference } from '@/utilities/calculateTimeframes';
import { isSkippedStop } from '../util/isSkippedStop';
import { UserContext } from '@/components/App';
import { IconButton } from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import SettingsIcon from '@material-ui/icons/Settings';
import NearMeIcon from '@material-ui/icons/NearMe';
import { calcStopTruckCubes, calcStopVolumes } from '@/utilities/calculateTruckStopCubes';
import { CDS_TIMEFRAME_EXPORT } from '@/constants/featureFlags';
import csvDownload from 'json-to-csv-export';
import { createCdsExportRows, cdsRows } from '../util/createCdsExportRows';
import { changeResourceWarnings, routeLoad } from '../util/resourceCeilings';
import { routeDisplayName } from '@/utilities/routeNames';
import { getPinnedPrefix, enforcePinnedPrefix, getPinDropIds } from '../util/pinnedStops';
import { OnwardSwitch } from '@/styles/blocks';
import { toMinuteIso } from '@/utilities/toMinuteIso';

function PlanningStops() {
    const {
        user_id,
        circles,
        drivers,
        helpers,
        defaultServiceTime,
        defaultDelTimeframe,
        default_end_location,
        partner_carriers,
        partner_contractors,
        resources,
        preferences_route_time_limit,
        preferences_route_eod_limit,
        preferences_route_eod_limit_next_day,
        preferences_start_estimate,
        preferences_end_estimate,
        preferences_warehouse_return_estimate,
        preferences_bypass_optimize,
        locations,
        dispatch_settings,
        ...clientUser
    } = useClientUser();
    const tz = useBusinessTimezone();
    const { selectedRoute } = useContext(PlanningSelectionContext);
    const {
        setNotification,
        setError,
        setActionableRoute,
        setActionableOrder,
        setActionableStop,
        setModalOpen,
        setClashingRoutes,
        setClashingDriver,
    } = useContext(PlanningUiContext);
    const {
        state: { routes, variables: planningVariables },
        callbacks: { subscribeToMore },
    } = useContext(PlanningDataContext);
    const { sidebarCollapsed } = useContext(UserContext);
    const client = useApolloClient();

    const route = useMemo(() => {
        return routes?.find((route) => route.route_id === selectedRoute);
    }, [routes, selectedRoute]);

    const ordersByKey = useMemo(() => {
        return Object.fromEntries((route?.orders || []).map((mapping) => [mapping.order_id, mapping.order]));
    }, [route]);

    const partneredCarrier = useMemo(
        () => routePartner(route, { partner_carriers, partner_contractors }),
        [route, partner_carriers, partner_contractors]
    );

    const partnerOptions = useMemo(() => routePartnerOptions({ partner_contractors }), [partner_contractors]);

    const isPartnerRoute = useMemo(() => {
        return !!partneredCarrier;
    }, [partneredCarrier]);

    const resourcesById = useResourcesById();
    const resource = resourcesById[route?.resource_id];

    const resourcesForToday = useMemo(() => selectableResources(resources, route, tz), [route, resources, tz]);

    // A current resource that isn't scheduled that day is not an option, so the select shows None
    // rather than a value MUI can't find among its items.
    const selectedResourceId = resourcesForToday.some((r) => r.resource_id === resource?.resource_id)
        ? resource.resource_id
        : '';

    const [updateStops, { loading: stopsLoading }] = useMutation(UPDATE_ROUTE_STOPS, {
        // Stop/mapping lists self-heal from update_routes_by_pk's returning
        // snapshot (last root field + replace merges) for the ORDERED
        // storage key. applyUpdateStopsResult also evicts deleted stops and
        // maintains the argument-less stopsByRouteId key other pages read.
        context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
        update: updateStopsCacheHandler,
    });

    const [upsertRoute, { loading: routeUpsertLoading }] = useMutation(UPSERT_ROUTE, {
        update: (cache, { data: { insert_routes_one } }) => {
            if (insert_routes_one) {
                cache.modify({
                    fields: {
                        routes(existingRoutes = []) {
                            const newRouteRef = cache.identify(insert_routes_one);
                            if (newRouteRef) {
                                // Check if route already exists in cache
                                const exists = existingRoutes.some((ref) => ref.__ref === newRouteRef);
                                if (!exists) {
                                    return [...existingRoutes, { __ref: newRouteRef }];
                                }
                            }
                            return existingRoutes;
                        },
                    },
                });
            }
        },
        onError: (error) => {
            setError(error, 'Error updating route');
        },
    });

    const updateRoute = ({ route, orders, drivers }) => {
        const remaining = omit(route, ROUTE_READONLY_FIELDS);
        return upsertRoute({
            variables: {
                route: remaining,
                route_update_cols: Object.keys(remaining),
                drivers: drivers || [],
                orders: orders || [],
            },
        });
    };

    const { pushRouteToTracking } = useCallbacks();

    const routeExceedsTimeLimits = useRouteTimeLimits(
        route,
        preferences_route_time_limit,
        preferences_route_eod_limit,
        preferences_route_eod_limit_next_day
    );

    const routeExceedsWindowCeiling = useRouteExceedsWindowCeiling(route, ordersByKey);

    const ordersAwaitingCD = useInvalidEventStates(route);

    const [optimizing, setOptimizing] = useState(false);
    const loading = stopsLoading || routeUpsertLoading || optimizing;

    const driver = drivers.find((driver) => route?.driver_id === driver.teammate_id);
    const helper = drivers.find((driver) => route?.helper_id === driver.teammate_id);

    const stopSequenceEnriched = useMemo(() => {
        let sortedStops = FTLStopHelpers.getStopSequence(route?.stopsByRouteId);
        if (!sortedStops) return [];
        let enrichedStops = sortedStops.map((stop) => {
            const orders = (stop.orders || []).map((order_id) => ordersByKey[order_id]).filter((x) => !!x);
            return {
                ...stop,
                orders,
                exchangeCubes: calcStopVolumes(orders),
            };
        });
        return calcStopTruckCubes(enrichedStops);
    }, [route, ordersByKey]);

    // Memoized so each row's `stop` prop identity is stable across unrelated
    // re-renders — getStopSequence cloneDeeps every stop on each call.
    const renderedStops = useMemo(
        () => FTLStopHelpers.getStopSequence(route?.stopsByRouteId).filter((stop) => !stop.start && !stop.end),
        [route?.stopsByRouteId]
    );

    const inProgress = useMemo(() => {
        return (route?.stopsByRouteId || []).some((s) => s.stop_completion_time);
    }, [route]);

    const routeTimeZone = useMemo(() => {
        const order = route?.orders?.[0]?.order || {};
        const { zip } = genAttributes(order);
        const locationZip = locations?.[0]?.business_zip;
        return (
            zipcode_to_timezone.lookup(order?.[zip]) ||
            zipcode_to_timezone.lookup(locationZip) ||
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
    }, [route, locations]);

    const isLocalTimeZone = routeTimeZone !== Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [startAddressAutocomplete, setStartAddressAutocomplete] = useState(false);
    const [startAddress, setStartAddress] = useState(route?.stopsByRouteId?.find((s) => s.start)?.address || '');
    const [startAddressInfo, setStartAddressInfo] = useState({});
    const [endAddressAutocomplete, setEndAddressAutocomplete] = useState(false);
    const [endAddress, setEndAddress] = useState(route?.stopsByRouteId?.find((s) => s.end)?.address || '');
    const [endAddressInfo, setEndAddressInfo] = useState({});
    const [routeDetailsOpen, setRouteDetailsOpen] = useState(false);
    const [offsetWidth, setOffsetWidth] = useState(0);

    const isLocked = route && route.status !== routeStatusOptions.PLANNING.value;

    const totalItems = useItemQuantity(route?.orders?.map((mapping) => mapping.order) || []);

    // Live preview: PlanningStops only renders the selected route, so whenever orders
    // are checked on the map the header totals project what they'd become once those
    // orders are assigned, shown in the preview color until the assignment commits.
    const ordersToAdd = usePreviewOrders(route);
    const isPreview = ordersToAdd.length > 0;
    const previewColor = colors.blues.primary;
    const previewOrderObjects = useMemo(
        () => [...(route?.orders?.map((mapping) => mapping.order) || []), ...ordersToAdd],
        [route, ordersToAdd]
    );
    const previewItems = useItemQuantity(previewOrderObjects);

    const displayItems = isPreview ? previewItems : totalItems;

    // Cubes and weight as the ceiling checks measure them.
    const displayLoad = useMemo(() => routeLoad(route, ordersToAdd), [route, ordersToAdd]);
    const isOverCeiling = (used, ceiling) => !isPreview && Number(ceiling) > 0 && used > Number(ceiling);
    const loadColor = (used, ceiling) => (isPreview ? previewColor : isOverCeiling(used, ceiling) ? 'red' : 'inherit');

    const dateTimeFormatter = (time) => {
        const date = new Date(time);
        if (isLocalTimeZone) {
            return routeTimeFormatter.format(date);
        } else {
            return `${routeTimeFormatter.format(date)} (${routeTimeFormatterTZ(routeTimeZone).format(date)})`;
        }
    };

    useEffect(() => {
        //reload start/end location info on route change
        setStartAddress(route?.stopsByRouteId?.find((s) => s.start)?.address || '');
        setEndAddress(route?.stopsByRouteId?.find((s) => s.end)?.address || '');
    }, [route]);

    const routeHasMultiplePickups = useMemo(() => {
        const pickups = route?.stopsByRouteId?.filter((stop) => FTLStopHelpers.isWarehousePickup(stop))?.length || 0;
        return pickups > 1;
    }, [route]);

    const routeStartTime = useMemo(() => {
        if (!route) return undefined;

        if (route.start_time) return new Date(route.start_time);
        const utcDate = new Date(convertToISO(route.scheduled_delivery));

        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const browserLocalDate = new Date(utcDate.getTime() - getTimezoneOffset(localTimeZone, utcDate));

        const defaultStart = getDispatchSettingPreference(
            'preferences_default_start_time',
            dispatch_settings,
            route?.orders?.[0]?.order?.shipper_id
        );
        browserLocalDate.setHours(defaultStart?.hours ?? 7, defaultStart?.minutes ?? 0, 0, 0);

        if (routeTimeZone && localTimeZone !== routeTimeZone) {
            const routeLocalDate = new Date(
                browserLocalDate.getTime() +
                    getTimezoneOffset(localTimeZone, utcDate) -
                    getTimezoneOffset(routeTimeZone, utcDate)
            );
            return routeLocalDate;
        } else {
            return browserLocalDate;
        }
    }, [route?.start_time, route?.orders, routeTimeZone, dispatch_settings]);

    const totalTime = useMemo(
        () =>
            calcEstimatedRouteTime(route, {
                routeStartTime,
                preferences_start_estimate,
                preferences_warehouse_return_estimate,
                preferences_end_estimate,
            }),
        [
            route,
            routeStartTime,
            preferences_start_estimate,
            preferences_warehouse_return_estimate,
            preferences_end_estimate,
        ]
    );

    const getStartAddressInfo = useCallback(
        async (address) => {
            const addressInfo = await handleGeocode({ address });
            setStartAddressInfo(addressInfo);
            if (addressInfo.lat && addressInfo.lng) {
                const newStops = FTLStopHelpers.addStart(route, addressInfo);
                updateStops({
                    variables: {
                        route_id: route.route_id,
                        route_update: { need_to_optimize: true },
                        ...FTLStopHelpers.gqlStopUpdates(newStops, route),
                    },
                    onError: (error) => {
                        setError(error, 'Error setting route start location');
                    },
                });
            } else {
                setNotification({
                    severity: 'warning',
                    message: 'Failed to geocode start location, stop has not been added to route',
                });
            }
        },
        [route]
    );

    const getEndAddressInfo = useCallback(
        async (address) => {
            const addressInfo = await handleGeocode({ address });

            // If the end location address added is the same address as the final dropoff stop of a return, do not add it.
            const addressField = `${addressInfo.street}, ${addressInfo.city}, ${addressInfo.state}, ${addressInfo.zip}`;
            const returnDropoffs = route?.stopsByRouteId.filter((s) => FTLStopHelpers.isFinalDropoff(s));
            if (returnDropoffs.some((s) => s.address === addressField)) {
                setNotification({
                    severity: 'warning',
                    message:
                        'End location is equal to the final dropoff stop of a return order, skipping adding end location.',
                });
                return;
            }

            setEndAddressInfo(addressInfo);
            if (addressInfo.lat && addressInfo.lng) {
                const newStops = FTLStopHelpers.addEnd(route, addressInfo);
                updateStops({
                    variables: {
                        route_id: route.route_id,
                        route_update: { need_to_optimize: true },
                        ...FTLStopHelpers.gqlStopUpdates(newStops, route),
                    },
                    onError: (error) => {
                        setError(error, 'Error setting route end location');
                    },
                });
            } else {
                setNotification({
                    severity: 'warning',
                    message: 'Failed to geocode end location, stop has not been added to route',
                });
            }
        },
        [route]
    );

    const routeEndTime = useMemo(() => {
        if (!route?.stopsByRouteId) return;

        const stopSequence = FTLStopHelpers.getStopSequence(route.stopsByRouteId);

        const lastStop = stopSequence[stopSequence.length - 1];

        if (!lastStop?.del_window_start) return;

        const lastStopServiceTime = lastStop.service_time || 0;

        if (lastStop.end) {
            return new Date(lastStop.del_window_start).getTime();
        }

        return new Date(lastStop.del_window_start).getTime() + lastStopServiceTime;
    }, [route]);

    const handleSelectStartAddress = (address) => {
        setStartAddress(address);
        getStartAddressInfo(address);
    };

    const handleSelectEndAddress = (address) => {
        setEndAddress(address);
        getEndAddressInfo(address);
    };

    const handleBlurStartAddress = () => {
        if (!startAddress) {
            setStartAddressInfo({});
        } else {
            setStartAddressAutocomplete(false);
            if (!startAddressInfo.lat || !startAddressInfo.lng) {
                getStartAddressInfo(startAddress);
            }
        }
    };

    const handleBlurEndAddress = () => {
        if (!endAddress) {
            setEndAddressInfo({});
        } else {
            setEndAddressAutocomplete(false);
            if (!endAddressInfo.lat || !endAddressInfo.lng) {
                getEndAddressInfo(endAddress);
            }
        }
    };

    const handleOnClear = (type) => () => {
        const newStops = FTLStopHelpers.removeCustomStop(route.stopsByRouteId, type);
        updateStops({
            variables: {
                route_id: route.route_id,
                route_update: { need_to_optimize: true },
                ...FTLStopHelpers.gqlStopUpdates(newStops, route),
            },
            onCompleted: () => {
                if (type === 'start') {
                    setStartAddress('');
                    setStartAddressInfo({});
                } else {
                    setEndAddress('');
                    setEndAddressInfo({});
                }
            },
            onError: (error) => {
                setError(error, `Error removing route ${type} location`);
            },
        });
    };

    const validateDrag = (result) => {
        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: 'You cannot re-order stops after a route has been started',
            });
            return { validateResult: false, stop0: null };
        }

        if (optimizing) {
            return { validateResult: false, stop0: null };
        }

        const { destination, source } = result;

        if (!destination) {
            return { validateResult: false, stop0: null };
        }
        let fromIdx = source.index;
        let toIdx = destination.index;

        if (destination.index === source.index) {
            return { validateResult: false, stop0: null };
        }

        const stopSequence = FTLStopHelpers.getStopSequence(route.stopsByRouteId);
        if (stopSequence[0].start) {
            fromIdx += 1;
            toIdx += 1;
        }
        const selectedStop = stopSequence[fromIdx];

        // Disable dragging for pickup stops if route is overnight. It doesn't matter anyway and just makes code more cumbersome.
        if (selectedStop.overnight && FTLStopHelpers.isWarehousePickup(selectedStop)) {
            setNotification({
                severity: 'warning',
                message: 'You cannot re-order overnight pickup stops.',
            });
            return { validateResult: false, stop0: null };
        }

        const stopsToCheck =
            fromIdx < toIdx ? stopSequence.slice(fromIdx + 1, toIdx + 1) : stopSequence.slice(toIdx, fromIdx);

        for (const stop of stopsToCheck) {
            if (stop.stop_completion_time) {
                setNotification({
                    severity: 'warning',
                    message: 'Cannot rearrange completed stops.',
                });
                return { validateResult: false, stop0: null };
            }

            if (fromIdx < toIdx && FTLStopHelpers.isFinalDropoff(stop)) {
                setNotification({
                    severity: 'warning',
                    message: `Cannot manipulate final dropoff.`,
                });
                return { validateResult: false, stop0: null };
            }

            if (fromIdx > toIdx && stop.type === 'PICKUP' && selectedStop.type === 'DROPOFF') {
                for (const dropoffOrderKey of selectedStop.orders) {
                    if (stop.orders.includes(dropoffOrderKey)) {
                        setNotification({
                            severity: 'warning',
                            message: `Cannot place an order's dropoff address before its pickup address.`,
                        });
                        return { validateResult: false, stop0: null };
                    }
                }
            } else if (stop.type === 'DROPOFF' && selectedStop.type === 'PICKUP') {
                for (const pickupOrderKey of selectedStop.orders) {
                    if (stop.orders.includes(pickupOrderKey)) {
                        setNotification({
                            severity: 'warning',
                            message: `Cannot place an order's pickup address after its dropoff address.`,
                        });
                        return { validateResult: false, stop0: null };
                    }
                }
            }
        }

        return { validateResult: true, stop0: stopSequence[0] };
    };

    const onDragEnd = (result) => {
        let { validateResult, stop0 } = validateDrag(result);
        if (!validateResult) {
            return;
        }

        const { destination, source, draggableId } = result;
        const newStops = _.cloneDeep(route.stopsByRouteId);
        let fromIdx = source.index;
        let toIdx = destination.index;

        if (stop0.start) {
            fromIdx += 1;
            toIdx += 1;
        }

        for (const stop of newStops) {
            if (stop.ordering === fromIdx && (stop.stop_id === draggableId || stop.type === 'LUNCH')) {
                stop.ordering = toIdx;
            }
            if (
                fromIdx < toIdx &&
                stop.ordering > fromIdx &&
                stop.ordering <= toIdx &&
                stop.stop_id !== draggableId &&
                stop.type !== 'LUNCH'
            ) {
                stop.ordering -= 1;
            }
            if (
                fromIdx > toIdx &&
                stop.ordering >= toIdx &&
                stop.ordering < fromIdx &&
                stop.stop_id !== draggableId &&
                stop.type !== 'LUNCH'
            ) {
                stop.ordering += 1;
            }
        }

        const stopSequence = FTLStopHelpers.getStopSequence(newStops);

        // Reordering can break the "pinned = contiguous prefix" rule. Any pinned
        // stop that now sits after an un-pinned customer stop loses its pin.
        const pinDropIds = getPinDropIds(stopSequence, route);
        if (pinDropIds.size) {
            stopSequence.forEach((stop) => {
                if (pinDropIds.has(stop.stop_id)) stop.pinned = false;
            });
        }

        const stopMovedUp = toIdx - fromIdx > 0;

        const ordersThatNeedRecalculating = (stopMovedUp ? stopSequence.slice(fromIdx) : stopSequence.slice(toIdx))
            .filter((stop) => FTLStopHelpers.isCustomerStop(stop, route))
            .reduce((acc, next) => {
                return [...acc, ...(next?.orders || [])];
            }, []);

        const optimisticStops = stopSequence.map((s) => ({ ...TEMP_STOP, ...s, __typename: 'stops' }));
        const optimisticOrders = ordersThatNeedRecalculating
            .map((order_id) => ordersByKey[order_id])
            .filter(Boolean)
            .map((order) => ({ ...order, delivery_time_confirmed: 'recalculate', __typename: 'orders' }));

        updateStops({
            variables: {
                route_id: route.route_id,
                route_update: {
                    need_to_optimize: true,
                },
                order_updates: [
                    {
                        where: { order_id: { _in: ordersThatNeedRecalculating } },
                        _set: {
                            delivery_time_confirmed: 'recalculate',
                        },
                    },
                ],
                ...FTLStopHelpers.gqlStopUpdates(stopSequence, route),
            },
            optimisticResponse: buildUpdateStopsOptimistic({
                route,
                stops: optimisticStops,
                stopsUpdated: optimisticStops,
                ordersUpdated: optimisticOrders,
            }),
            onError: (error) => {
                setError(error, 'Error reordering stops');
                // A later gesture may have queued behind this one (same
                // serializationKey) and built its full-replace payload on top
                // of this failed reorder's optimistic effect. Refetch to
                // converge the board back to server truth.
                client
                    .query({
                        query: QUERY_PLANNING_DATA,
                        variables: planningVariables,
                        fetchPolicy: 'network-only',
                        context: { queryDeduplication: false },
                    })
                    .catch(() => {});
            },
        });
    };

    // Stable identities for the memoized StopListItem rows. onDragEnd and
    // pushRouteToTracking close over per-render state (route, optimizing,
    // planningVariables, ...), so a deps-based useCallback is not viable —
    // trampoline through refs instead.
    const onDragEndRef = useRef();
    onDragEndRef.current = onDragEnd;
    const stableOnDragEnd = useCallback((result) => onDragEndRef.current(result), []);
    const pushRouteToTrackingRef = useRef();
    pushRouteToTrackingRef.current = pushRouteToTracking;
    const stablePushRouteToTracking = useCallback((...args) => pushRouteToTrackingRef.current(...args), []);

    const handleChangeStartTime = (e) => {
        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: 'Cannot change the start time for a route that has already been started.',
            });
        } else {
            updateRoute({
                route: {
                    ...route,
                    start_time: new Date(e.target.value).toISOString(),
                    need_to_optimize: true,
                },
            });
        }
    };

    const routeDriverComparison = (newRoute, otherRoute, _driver) => {
        // BOTH routes must have start times, must have stops, and must be optimized for this check to plausible.
        if (
            otherRoute &&
            otherRoute.start_time &&
            newRoute.start_time &&
            otherRoute?.stopsByRouteId?.[0]?.del_window_end &&
            newRoute?.stopsByRouteId?.[0]?.del_window_end &&
            !otherRoute.need_to_optimize &&
            !newRoute.need_to_optimize
        ) {
            // If other route is completed early, then the driver may be re-assigned regardless of scheduled timeframes.
            if (otherRoute.status === 'complete') {
                return false;
            }

            const lastStopServiceTime =
                otherRoute.stopsByRouteId[otherRoute.stopsByRouteId.length - 1]?.service_time ||
                defaultServiceTime * 60 * 1000;

            const startTimeOfOtherRoute = new Date(otherRoute.start_time);
            const endTimeOfOtherRoute = new Date(
                new Date(otherRoute.stopsByRouteId[otherRoute.stopsByRouteId.length - 1].del_window_end).getTime() +
                    lastStopServiceTime
            );
            const startTimeOfNewRoute = new Date(newRoute.start_time);
            const endTimeOfNewRoute = new Date(
                new Date(newRoute.stopsByRouteId[newRoute.stopsByRouteId.length - 1].del_window_end).getTime() +
                    lastStopServiceTime
            );

            // If one route starts between the other route beginning and ending(+30 min)
            // Then notify the user
            if (
                (startTimeOfNewRoute >= startTimeOfOtherRoute &&
                    startTimeOfNewRoute - endTimeOfOtherRoute < 1000 * 60) ||
                (startTimeOfOtherRoute >= startTimeOfNewRoute && startTimeOfOtherRoute - endTimeOfNewRoute < 1000 * 60)
            ) {
                setModalOpen(MODALS.DRIVER_OVERLAP);
                setClashingRoutes([newRoute.route_number, otherRoute.route_number]);
                setClashingDriver(_driver);
                return true;
            }
        }
        return false;
    };

    const handleChangeResource = (resource_id) => {
        const nextResourceId = resource_id || null;
        const nextResource = nextResourceId ? resourcesForToday.find((r) => r.resource_id === nextResourceId) : null;

        const warnings = changeResourceWarnings({ route, routes, nextResourceId, nextResource });
        if (warnings.length > 0) {
            setNotification({ severity: 'warning', message: warnings });
        }

        // Only the delivery leg carries the resource, never the pickup leg.
        const order_updates = (route?.orders || [])
            .filter((mapping) => mapping?.order_id && mapping.type !== 'PICKUP')
            .map((mapping) => ({
                where: { order_id: { _eq: mapping.order_id } },
                _set: { resource_id: nextResourceId },
            }));

        // With no orders this may still be a shell, and update_routes_by_pk would silently no-op on
        // a route that isn't in the DB yet.
        if (order_updates.length === 0) {
            updateRoute({
                route: {
                    ...route,
                    resource_id: nextResourceId,
                },
            });
            return;
        }

        // One transaction, so a rejected write can't leave the route and its orders pointing at
        // different resources.
        updateStops({
            variables: {
                route_id: route.route_id,
                route_update: { resource_id: nextResourceId },
                order_updates,
            },
            onError: (error) => {
                setError(error, 'Error updating route');
            },
        });
    };

    const handleChangeDriver = (driverId, isHelper = false) => {
        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: `Cannot change the ${
                    isHelper ? 'helper' : 'driver'
                } for a route that has already been started.`,
            });
        } else {
            const attr = isHelper ? 'helper_id' : 'driver_id';
            if (driverId) {
                const otherRoute = routes.find((r) => r.route_id !== route.route_id && r[attr] === driverId);
                const driverTimeClash = routeDriverComparison(route, otherRoute, driverId);
                if (driverTimeClash) return;
            }

            updateRoute({
                route: {
                    ...route,
                    [attr]: driverId,
                    carrier_id: null,
                    sending_to: 'internal',
                    oms: true,
                },
            });
        }
    };

    const handleCarrierSelect = (e) => {
        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: 'Cannot change the assigned carrier for a route that has already been started.',
            });
        } else {
            const newCarrier = e.target.value === 'NONE' ? null : e.target.value;
            updateRoute({
                route: {
                    ...route,
                    sending_to: newCarrier ? 'partner' : 'internal',
                    carrier_id: newCarrier,
                    driver_id: null,
                    oms: false,
                },
                orders: partnerAssignmentUpdates({
                    orders: route.orders,
                    partnerId: newCarrier,
                    partnerType: partnerTypeFor(partnerOptions, newCarrier),
                    userId: user_id,
                }),
            });
        }
    };

    const handlePartnerDriverSelect = (e) => {
        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: 'Cannot change the assigned driver for a route that has already been started.',
            });
        } else {
            const newDriver = e.target.value;
            updateRoute({
                route: {
                    ...route,
                    driver_id: newDriver !== 'NONE' ? newDriver : null,
                },
            });
        }
    };

    const handleOptimizeRoute = async () => {
        if (loading) return;

        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: 'Cannot optimize a route that has already been started.',
            });
        } else if (route.stopsByRouteId.some((s) => s.stop_completion_time)) {
            setNotification({
                severity: 'warning',
                message: 'Cannot optimize a route that already has completed stops.',
            });
        } else if (!routeStartTime) {
            setNotification({
                severity: 'warning',
                message: 'Cannot optimize a route until a start time has been selected.',
            });
        } else if (route.stopsByRouteId.some((s) => s.overnight) && !startAddress) {
            setNotification({
                severity: 'warning',
                message: 'An route with overnight stops requires a start address to optimize.',
            });
        } else {
            try {
                setOptimizing(true);

                if (
                    route.stopsByRouteId.find(
                        (stop) => stop.type === 'LUNCH' && (!stop.earliest_start || !stop.latest_end)
                    )
                ) {
                    setNotification({
                        severity: 'warning',
                        message: 'Lunch stop must have an optimized window set',
                    });
                    setOptimizing(false);
                    return;
                }

                const ordersByKey = Object.fromEntries(
                    (route.orders || []).map((mapping) => [mapping.order_id, mapping.order])
                );
                const stopsWithSvcTimes = route.stopsByRouteId.map((stop) => {
                    let service_time;
                    if (stop.service_time || stop.service_time === 0) {
                        service_time = stop.service_time;
                    } else {
                        const { hours, minutes } = calcDefaultServiceTime(stop, ordersByKey, dispatch_settings);
                        service_time = ((hours || 0) * 60 + (minutes || 0)) * 60000;
                    }

                    return {
                        ...stop,
                        service_time,
                    };
                });

                // Pinning: send the locked prefix so the optimizer can start its
                // sub-problem at the last pinned stop. It holds the stops the
                // driver has already served out of the problem and returns them
                // ahead of the optimized remainder.
                const pinnedPrefix = getPinnedPrefix(route);

                const response = await post(MAPBOX_OPTIMIZE, {
                    route: {
                        route_id: route.route_id,
                        start_time: routeStartTime,
                        stops: stopsWithSvcTimes,
                    },
                    options: {
                        pinned_stop_ids: pinnedPrefix.map((stop) => stop.stop_id),
                        buffer: clientUser.preferences_timeframe_buffer_apply
                            ? clientUser.preferences_timeframe_buffer
                            : 0,
                        preferences_timeframe_buffer_apply: clientUser.preferences_timeframe_buffer_apply,
                        round: clientUser.preferences_timeframe_round,
                        window_length: clientUser.preferences_delivery_timeframe,
                        preferences_delivery_window_floor: clientUser.preferences_delivery_window_floor,
                        useStartInEstimate: preferences_start_estimate,
                        useEndInEstimate: preferences_end_estimate,
                        finish_returns_next_day: route?.finish_returns_next_day,
                        dispatch_settings: dispatch_settings,
                        client_id: user_id,
                    },
                    ordersByKey: ordersByKey,
                });

                const { stops: stopsWithTimeframes, summary, requires_retiming } = response.data;

                // Guard against a response that breaks the pinned prefix. No-op
                // when nothing is pinned.
                const finalStops = enforcePinnedPrefix({
                    optimizedStops: stopsWithTimeframes,
                    pinnedPrefix,
                    route,
                });

                // The optimizer only sequenced part of the route whenever stops
                // were held out of the problem — the pinned prefix, or the
                // head/tail bookends the backend holds to keep warehouse
                // pickups and final returns fixed in place. Held stops come
                // back without timings and the rest are timed relative to a
                // sub-route, so optimization has only decided the ORDER:
                // re-time the full sequence via Map Route so every stop gets a
                // correct timeframe in its final position.
                //
                // requires_retiming covers the bookend case on its own, but an
                // older function build will not send it, so the pinned prefix
                // is still checked directly.
                if (pinnedPrefix.length || requires_retiming) {
                    await runMapRouteTimeframes(finalStops);
                    return;
                }

                const updates = stopsWithTimeframes
                    .filter((stop) => FTLStopHelpers.isCustomerStop(stop, route))
                    .map((stop) => {
                        return {
                            where: { order_id: { _in: stop.orders } },
                            _set: {
                                del_window_start: toMinuteIso(stop.del_window_start),
                                del_window_end: toMinuteIso(stop.del_window_end),
                                ...(stop.orders.some(
                                    (order_id) => ordersByKey[order_id]?.delivery_time_confirmed === 'recalculate'
                                )
                                    ? { delivery_time_confirmed: null, confirmation_source: null }
                                    : {}),
                            },
                        };
                    });

                updateStops({
                    variables: {
                        route_id: route.route_id,
                        route_update: {
                            start_time: routeStartTime,
                            need_to_optimize: false,
                            need_to_submit: true,
                            estimated_driving_distance: `${parseInt(
                                summary.total_driving_distance * 0.000621371
                            )} miles`,
                            estimated_driving_time: hourMinuteFormatter.format(summary.total_driving_time / 60000),
                        },
                        order_updates: updates,
                        ...FTLStopHelpers.gqlStopUpdates(finalStops, route),
                    },
                    onCompleted: () => {
                        setOptimizing(false);
                    },
                    onError: (error) => {
                        setError(error, 'Error optimizing route');
                        setOptimizing(false);
                    },
                });
            } catch (error) {
                if (error?.response?.data?.error) {
                    setNotification({
                        severity: 'error',
                        message: error.response.data.error,
                    });
                } else {
                    Sentry.captureException(error);
                    console.error(error);
                    setNotification({
                        severity: 'error',
                        message: 'Failed to optimize route',
                    });
                }
                setOptimizing(false);
            }
        }
    };

    // Compute Google-directions timeframes for a FIXED stop order and write
    // them (no re-ordering). Extracted from handleMapRoute so it can also run
    // after optimization to timeframe the full pinned-stops sequence. Callers
    // are responsible for the isLocked / start-time / loading guards.
    const runMapRouteTimeframes = async (newStops) => {
        setOptimizing(true);

        try {
            let stopSequence = FTLStopHelpers.getStopSequence(newStops || route.stopsByRouteId);
            const directionsData = await FTLStopHelpers.getGMapsDirections(stopSequence, new Date(routeStartTime));
            const directionsRoute = directionsData?.routes?.[0];

            if (directionsData?.status === 'ZERO_RESULTS') {
                setNotification({
                    severity: 'error',
                    message:
                        'Google directions cannot find a route found between the pickup and dropoff location. Please change the dropoff address to a nearby location and provide detailed instructions for drivers to locate the stop.',
                });
                setOptimizing(false);
                return;
            }

            let directionsExepctedLength = stopSequence.length - 1;
            stopSequence.forEach((s) => {
                if (isSkippedStop(s)) directionsExepctedLength--;
            });

            if (directionsRoute?.legs?.length !== directionsExepctedLength) {
                setNotification({
                    severity: 'error',
                    message:
                        'Google directions cannot find a route found between the pickup and dropoff location. Please change the dropoff address to a nearby location and provide detailed instructions for drivers to locate the stop.',
                });
                setOptimizing(false);
                return;
            }

            if (!directionsData.useTrafficEstimates) {
                setNotification({
                    severity: 'info',
                    message:
                        'Route Start Time is in the past. Cannot use traffic predictions, defaulting to historical average.',
                });
            }

            const ordersByKey = Object.fromEntries(
                (route.orders || []).map((mapping) => [mapping.order_id, mapping.order])
            );

            const { stopsWithTimeframes, estimatedDrivingDistance, estimatedDrivingTime } = calculateGoogleTimeframes(
                stopSequence,
                directionsRoute.legs,
                new Date(routeStartTime),
                ordersByKey,
                directionsData.useTrafficEstimates,
                clientUser,
                default_end_location,
                preferences_start_estimate,
                preferences_warehouse_return_estimate,
                preferences_end_estimate,
                dispatch_settings
            );

            const updates = stopsWithTimeframes
                .filter((stop) => FTLStopHelpers.isCustomerStop(stop, route))
                .map((stop) => {
                    return {
                        where: { order_id: { _in: stop.orders } },
                        _set: {
                            del_window_start: toMinuteIso(stop.del_window_start),
                            del_window_end: toMinuteIso(stop.del_window_end),
                            ...(stop.orders.some(
                                (order_id) => ordersByKey[order_id]?.delivery_time_confirmed === 'recalculate'
                            )
                                ? { delivery_time_confirmed: null, confirmation_source: null }
                                : {}),
                        },
                    };
                });

            if (route.driver_id) {
                const otherRoute = routes.find((r) => r.route_id !== route.route_id && r.driver_id === route.driver_id);
                const _driver = drivers.find((i) => route?.driver_id === i.teammate_id);
                const driverTimeClash = routeDriverComparison(
                    { ...route, stopsByRouteId: stopsWithTimeframes, need_to_optimize: false },
                    otherRoute,
                    _driver
                );
                if (driverTimeClash) {
                    setOptimizing(false);
                    return;
                }
            }

            updateStops({
                variables: {
                    route_id: route.route_id,
                    route_update: {
                        start_time: routeStartTime,
                        need_to_optimize: false,
                        need_to_submit: true,
                        estimated_driving_distance: estimatedDrivingDistance,
                        estimated_driving_time: estimatedDrivingTime,
                    },
                    order_updates: updates,
                    ...FTLStopHelpers.gqlStopUpdates(stopsWithTimeframes, route),
                },
                onCompleted: () => {
                    setOptimizing(false);
                },
                onError: (error) => {
                    setError(error, 'Error getting route directions');
                    setOptimizing(false);
                },
            });
        } catch (error) {
            setError(error, 'Error mapping route');
            setOptimizing(false);
        }
    };

    const handleMapRoute = async (newStops) => {
        if (loading) return;

        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: 'Cannot map a route that has already been started.',
            });
        } else if (!routeStartTime) {
            setNotification({
                severity: 'warning',
                message: 'Cannot map a route until a start time has been selected.',
            });
        } else {
            await runMapRouteTimeframes(newStops);
        }
    };

    const handleReverseStops = () => {
        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: 'Cannot reverse stops for a route that has already been started.',
            });
        } else {
            const reversedStops = FTLStopHelpers.reverseStops(route.stopsByRouteId);

            // Reversing moves the pinned front stops to the back, breaking the
            // "pinned = contiguous prefix" rule — drop those pins.
            const pinDropIds = getPinDropIds(reversedStops, route);
            if (pinDropIds.size) {
                reversedStops.forEach((stop) => {
                    if (pinDropIds.has(stop.stop_id)) stop.pinned = false;
                });
            }

            handleMapRoute(reversedStops);
        }
    };

    const handleSubmitCheck = (sendingTo) => {
        if (isLocked) {
            setNotification({
                severity: 'warning',
                message: `Cannot submit a route that has already been started.`,
            });
            return false;
        } else if (routeIsMissingTimeframes(route)) {
            setNotification({
                severity: 'warning',
                message: 'Route is missing time frames. Please click "Map Route" or "Optimize" to add them.',
            });
            return false;
        } else if (routeNeedsRecalculating(route)) {
            setNotification({
                severity: 'warning',
                message:
                    'Stops have been rearranged. Please click "Map Route" or "Optimize" to recalculate timeframes.',
            });
            return false;
        } else if (route.need_to_optimize && (sendingTo === 'marketplace' || !preferences_bypass_optimize)) {
            setNotification({
                severity: 'warning',
                message:
                    'You have made changes to this route that require timeframes to be recalculated. Please click "Map Route" or "Optimize" to recalculate timeframes.',
            });
            return false;
        }

        return true;
    };

    const handleSendToOnward = () => {
        const sendingTo = 'marketplace';
        if (handleSubmitCheck(sendingTo)) {
            const invalidOrder = (route.orders || []).find((mapping) => {
                const [, hasMissingField] = validate({ ...mapping.order, oms: false });
                return hasMissingField?.length > 0;
            })?.order;
            if (invalidOrder) {
                setActionableOrder({ ...invalidOrder, oms: false });
                setModalOpen(MODALS.ORDER_EDIT);

                setNotification({
                    severity: 'warning',
                    message: `Cannot submit to Onward, order incomplete`,
                });

                return;
            }

            if (routeIsConfirmed(route)) {
                pushRouteToTracking(route, sendingTo);
            } else {
                setActionableRoute({ ...route, sending_to: sendingTo });
                setModalOpen(MODALS.ROUTE_MARKETPLACE);
            }
        }
    };

    const handleSendToInternal = () => {
        const sendingTo = isPartnerRoute ? 'partner' : 'internal';
        if (handleSubmitCheck(sendingTo)) {
            if (routeIsConfirmed(route)) {
                pushRouteToTracking(route, sendingTo);
            } else {
                setActionableRoute({ ...route, sending_to: sendingTo });
                setModalOpen(MODALS.ROUTE_INTERNAL);
            }
        }
    };

    const handleCdsExport = () => {
        const data = (route.orders || []).map((mapping) => {
            const order = mapping.order;
            const stop = route?.stopsByRouteId?.find(
                (s) => FTLStopHelpers.isCustomerStop(s, route) && s.orders?.includes(order.order_id)
            );
            return {
                order,
                stop,
            };
        });

        const csvData = createCdsExportRows(data);

        csvDownload({
            data: csvData,
            filename: `${routeDisplayName(route)} CDS Export`,
            headers: cdsRows.map((option) => option.display),
            delimiter: ',',
        });
    };

    const StopMetadata = ({ label, value, defaultValue }) => {
        return (
            <GridItemRow>
                {label && <Body2>{label}</Body2>}
                <Body1
                    css={css`
                        text-align: right;
                    `}
                >
                    {value || defaultValue}
                </Body1>
            </GridItemRow>
        );
    };

    const stopCount = useCustomerStops(route);
    const displayStopCount = isPreview ? displayLoad.stops : stopCount;

    const lunchStop = route?.stopsByRouteId?.find((stop) => stop.type === 'LUNCH');

    const partnerDrivers = useMemo(() => {
        if (!partneredCarrier) return [];
        //iterate through partnereredCarrier and get all users who usertype as driver
        const partnerDrivers = [];
        partneredCarrier?.user?.teammatesByLeaderId?.forEach((user) => {
            if (user?.roles?.driver) {
                partnerDrivers.push(user);
            }
        });
        return partnerDrivers;
    }, [partneredCarrier]);

    // Assigned driver's display name, covering both internal drivers and
    // partner-carrier drivers (route.driver_id can point at either set).
    const driverName = useMemo(() => {
        if (!route?.driver_id) return null;
        return (
            drivers.find((d) => d.teammate_id === route.driver_id)?.username ||
            partnerDrivers.find((d) => d.teammate_id === route.driver_id)?.username ||
            null
        );
    }, [route?.driver_id, drivers, partnerDrivers]);

    const [anchor, setAnchor] = useState(null);
    const anchorRef = useRef(null);
    const containerRef = useRef(null);

    if (!route) return null;

    return (
        <>
            <PlanningListItem
                ref={anchorRef}
                css={css`
                    box-shadow: 0px 4px 4px 0px #00000040;
                    z-index: 5;
                `}
            >
                {routeExceedsTimeLimits && (
                    <GridItemRow>
                        <Body1
                            css={css`
                                color: ${colors.reds.primary};
                                text-align: center;
                            `}
                        >
                            Warning: Route exceeds configured time thresholds
                        </Body1>
                    </GridItemRow>
                )}

                {routeExceedsWindowCeiling && (
                    <GridItemRow>
                        <Body1
                            css={css`
                                color: ${colors.oranges.primary};
                                text-align: center;
                            `}
                        >
                            Warning: Route has stops whose delivery window ends past your ceiling
                        </Body1>
                    </GridItemRow>
                )}

                {ordersAwaitingCD && (
                    <GridItemRow>
                        <Body1
                            css={css`
                                color: ${colors.reds.primary};
                                text-align: center;
                            `}
                            onClick={() => setModalOpen(MODALS.AWAITING_CD)}
                        >
                            Warning: This route has cross-docked orders with incomplete pickup legs.{' '}
                            <span
                                css={css`
                                    text-decoration: underline;
                                    cursor: pointer;
                                `}
                            >
                                More Info
                            </span>
                        </Body1>
                    </GridItemRow>
                )}

                <GridItemRow
                    css={css`
                        margin-bottom: 8px !important;
                    `}
                >
                    <H1
                        css={css`
                            font-weight: 800;
                            margin-bottom: 0;
                        `}
                    >
                        {routeDisplayName(route)}
                    </H1>
                </GridItemRow>
                <GridItemRow
                    css={css`
                        margin-bottom: 4px !important;
                    `}
                >
                    <Body2
                        css={
                            isPreview
                                ? css`
                                      color: ${previewColor};
                                  `
                                : undefined
                        }
                    >
                        {integerFormatter.format(displayItems)} items /{' '}
                        <span style={{ color: loadColor(displayLoad.cubes, resource?.capacity_cubes) }}>
                            {integerFormatter.format(displayLoad.cubes)} cu ft.
                        </span>{' '}
                        /{' '}
                        <span style={{ color: loadColor(displayLoad.weight, resource?.capacity_weight) }}>
                            {poundFormatter.format(displayLoad.weight)}
                        </span>{' '}
                        / {displayStopCount} stop
                        {displayStopCount !== 1 ? 's' : ''}{' '}
                    </Body2>
                </GridItemRow>

                <GridItemRow
                    css={css`
                        margin-bottom: 16px !important;
                        padding-bottom: 16px !important;
                        border-bottom: 1px solid #e2e2e2;
                    `}
                >
                    <Body2>Driver: {driverName || 'Unassigned'}</Body2>
                </GridItemRow>

                <GridItemRow
                    css={css`
                        margin-bottom: 12px !important;
                    `}
                >
                    <Button
                        onClick={() => setAnchor((prev) => (prev ? null : anchorRef?.current))}
                        variant="contained"
                        css={css`
                            color: black !important;
                            background-color: white !important;
                            border: 1px solid #a6a6a6;
                            margin-right: auto;
                        `}
                    >
                        Route Details
                    </Button>

                    {!isLocked && (
                        <>
                            {loading && <CircularProgress size={25} sx={{ color: 'darkgrey' }} />}

                            <RouteDetailsIconButton
                                onClick={() => handleOptimizeRoute()}
                                disabled={
                                    !route.stopsByRouteId?.length ||
                                    loading ||
                                    routeStartTime === '1970-01-01T00:00:00+00:00'
                                }
                                Icon={SettingsIcon}
                                loading={loading}
                                tooltip={'Optimize Route'}
                                css={css`
                                    margin-left: 21px;
                                `}
                            />

                            <RouteDetailsIconButton
                                onClick={() => handleMapRoute()}
                                disabled={
                                    !route.stopsByRouteId?.length ||
                                    loading ||
                                    routeStartTime === '1970-01-01T00:00:00+00:00'
                                }
                                Icon={NearMeIcon}
                                tooltip={'Map Route'}
                                css={css`
                                    margin-left: 21px;
                                `}
                            />

                            <RouteDetailsIconButton
                                onClick={handleReverseStops}
                                Icon={SwapVert}
                                disabled={loading || routeHasMultiplePickups}
                                tooltip={'Reverse stop order'}
                                disabledTooltip={'Reverse stop order disabled because of multiple pick ups.'}
                                css={css`
                                    margin-left: 21px;
                                `}
                            />
                        </>
                    )}
                </GridItemRow>
            </PlanningListItem>

            <GrowingGrid container>
                <GridScrollY>
                    <StopBookend
                        label="Start Location"
                        open={startAddressAutocomplete}
                        value={startAddress}
                        stop={route?.stopsByRouteId?.find((stop) => stop.start)}
                        timeZone={routeTimeZone}
                        callbacks={{
                            onInput: (address) => setStartAddress(address),
                            onSelect: handleSelectStartAddress,
                            onFocus: () => setStartAddressAutocomplete(true),
                            onBlur: handleBlurStartAddress,
                            onClear: handleOnClear('start'),
                        }}
                        isLocked={isLocked}
                        isLoading={loading}
                        circles={circles}
                        overnight={route?.stopsByRouteId?.some((s) => !!s.overnight) || false}
                        routeLoading={routeUpsertLoading || optimizing}
                    />

                    {/* Stops */}
                    <DragDropContext onDragEnd={stableOnDragEnd}>
                        <StrictModeDroppable droppableId="planning-stops">
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    css={css`
                                        width: 100%;
                                        min-height: 32px;
                                    `}
                                >
                                    {renderedStops.map((stop, idx) => (
                                        <Draggable
                                            isDragDisabled={isLocked || FTLStopHelpers.isFinalDropoff(stop)}
                                            key={`${stop.stop_id}`}
                                            draggableId={`${stop.stop_id}`}
                                            index={idx}
                                        >
                                            {(_provided, _snapshot) => (
                                                <div
                                                    {..._provided.draggableProps}
                                                    {..._provided.dragHandleProps}
                                                    ref={_provided.innerRef}
                                                >
                                                    <StopListItem
                                                        stop={stop}
                                                        route={route}
                                                        resource={resource}
                                                        isLocked={isLocked}
                                                        stopSequenceEnriched={stopSequenceEnriched}
                                                        subscribeToMore={subscribeToMore}
                                                        onDragEnd={stableOnDragEnd}
                                                        pushRouteToTracking={stablePushRouteToTracking}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>

                    <StopBookend
                        label="End Location"
                        open={endAddressAutocomplete}
                        value={endAddress}
                        stop={route?.stopsByRouteId?.find((stop) => stop.end)}
                        timeZone={routeTimeZone}
                        callbacks={{
                            onInput: (address) => setEndAddress(address),
                            onSelect: handleSelectEndAddress,
                            onFocus: () => setEndAddressAutocomplete(true),
                            onBlur: handleBlurEndAddress,
                            onClear: handleOnClear('end'),
                        }}
                        isLocked={isLocked}
                        isLoading={loading}
                        location={stopCount > 0 ? 'top' : 'bottom'}
                    />
                </GridScrollY>
            </GrowingGrid>

            {!isLocked && (
                <Grid
                    container
                    css={css`
                        padding: 1rem;
                        justify-content: space-between;
                        row-gap: 0.5rem;
                        column-gap: 1rem;
                    `}
                >
                    {circles?.['ftl-marketplace'] && (
                        <PrimaryButton
                            css={css`
                                flex-grow: 1;
                            `}
                            onClick={handleSendToOnward}
                        >
                            Send to Onward
                        </PrimaryButton>
                    )}
                    <PrimaryButton
                        css={css`
                            flex-grow: 1;
                        `}
                        onClick={handleSendToInternal}
                    >
                        {partneredCarrier ? `Send to ${partneredCarrier?.business_name}` : 'Submit'}
                    </PrimaryButton>
                </Grid>
            )}

            <Popper open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} placement={'right-start'}>
                <PlanningListItem
                    css={css`
                        z-index: 4;
                        width: calc((100vw - ${sidebarCollapsed ? '50' : '246.21'}px) / 3);
                        box-shadow: 0px 4px 4px 0px #00000040;
                    `}
                >
                    <GridItemRow>
                        <GridItemRow>
                            {circles?.[CDS_TIMEFRAME_EXPORT] && (
                                <PrimaryButton onClick={handleCdsExport}>CDS Export</PrimaryButton>
                            )}
                        </GridItemRow>

                        <GridItemRow
                            css={css`
                                align-self: flex-end;
                                margin-bottom: 21px !important;
                                margin-top: 11px !important;
                            `}
                        >
                            <IconButton
                                onClick={() => {
                                    setAnchor(null);
                                }}
                                css={css`
                                    padding: 0 !important;
                                `}
                            >
                                <Clear
                                    css={css`
                                        color: black;
                                    `}
                                />
                            </IconButton>
                        </GridItemRow>
                    </GridItemRow>

                    <StopMetadata
                        label="Estimated Distance"
                        value={route?.estimated_driving_distance}
                        defaultValue="N/A"
                    />

                    <StopMetadata label="Estimated Route Time" value={totalTime} defaultValue="N/A" />

                    <StopMetadata
                        label="Estimated Driving Time"
                        value={route?.estimated_driving_time}
                        defaultValue="N/A"
                    />

                    {!isLocked && route.scheduled_delivery ? (
                        <GridItemRow>
                            <Body2>Pickup Arrival Time:</Body2>
                            <FormControl
                                css={css`
                                    min-width: 120px;
                                    flex-grow: 1;
                                    padding-left: 10px;

                                    .MuiInputLabel-formControl {
                                        position: static;
                                        transform: none;
                                    }
                                `}
                            >
                                <StartTimeSelect
                                    disabled={inProgress}
                                    onChange={handleChangeStartTime}
                                    value={routeStartTime.getTime()}
                                    timeZone={routeTimeZone}
                                    deliveryDate={route.scheduled_delivery}
                                    interval={30}
                                    css={css`
                                        width: auto !important;
                                        & .MuiFormControl-root {
                                            width: 100%;
                                        }
                                    `}
                                />
                            </FormControl>
                        </GridItemRow>
                    ) : (
                        <StopMetadata
                            label="Pickup Arrival Time"
                            value={routeStartTime ? dateTimeFormatter(routeStartTime) : 'N/A'}
                            defaultValue="N/A"
                        />
                    )}

                    {routeEndTime && (
                        <StopMetadata
                            label="Route End Time"
                            value={dateTimeFormatter(routeEndTime)}
                            defaultValue="N/A"
                        />
                    )}

                    <GridItemRow>
                        {lunchStop ? (
                            <>
                                <Body2>Lunch</Body2>
                                <Body1
                                    css={css`
                                        text-decoration: underline;
                                        cursor: pointer;
                                    `}
                                    onClick={(e) => setModalOpen(MODALS.DRIVER_LUNCH)}
                                >
                                    {`${hourMinuteFormatter.format(lunchStop.service_time / 60000)}, ${
                                        lunchStop.ordering === 1
                                            ? `after Pickup`
                                            : `after Stop ${lunchStop.ordering - 1}`
                                    }`}
                                </Body1>
                            </>
                        ) : (
                            <Body1
                                css={css`
                                    text-decoration: underline;
                                    cursor: pointer;
                                `}
                                onClick={(e) => setModalOpen(MODALS.DRIVER_LUNCH)}
                            >
                                Add Lunch
                            </Body1>
                        )}
                    </GridItemRow>

                    {drivers.length > 0 && !partneredCarrier ? (
                        <GridItemRow>
                            <Body2
                                css={css`
                                    width: 75px;
                                `}
                            >
                                Driver:{' '}
                            </Body2>

                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                select
                                onChange={(e) => handleChangeDriver(e.target.value === 'NONE' ? null : e.target.value)}
                                value={driver?.teammate_id || 'NONE'}
                                disabled={isLocked}
                            >
                                <MenuItem value={'NONE'}>
                                    {route.driver_id ? 'Remove driver' : 'Select a driver ...'}
                                </MenuItem>
                                {drivers.map((driver) => (
                                    <MenuItem key={driver.teammate_id} value={driver.teammate_id}>
                                        {driver.username}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </GridItemRow>
                    ) : null}
                    {helpers.length > 0 && !partneredCarrier ? (
                        <GridItemRow>
                            <Body2
                                css={css`
                                    width: 75px;
                                `}
                            >
                                Helper:{' '}
                            </Body2>

                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                select
                                onChange={(e) =>
                                    handleChangeDriver(e.target.value === 'NONE' ? null : e.target.value, true)
                                }
                                value={route.helper_id || 'NONE'}
                                disabled={isLocked}
                            >
                                <MenuItem value={'NONE'}>
                                    {route.helper_id ? 'Remove helper' : 'Select a helper ...'}
                                </MenuItem>
                                {helpers.map((helper) => (
                                    <MenuItem key={helper.teammate_id} value={helper.teammate_id}>
                                        {helper.username}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </GridItemRow>
                    ) : null}

                    {partnerOptions.length > 0 ? (
                        <>
                            <GridItemRow>
                                <Body2
                                    css={css`
                                        width: 75px;
                                    `}
                                >
                                    Carrier:{' '}
                                </Body2>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    select
                                    onChange={handleCarrierSelect}
                                    value={route?.carrier_id || 'NONE'}
                                    disabled={isLocked}
                                >
                                    <MenuItem value={'NONE'}>
                                        {route.carrier_id ? 'Remove carrier' : 'Select a carrier ...'}
                                    </MenuItem>
                                    {partnerOptions.map((partner) => (
                                        <MenuItem key={partner.client_id} value={partner.client_id}>
                                            {partner.business_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </GridItemRow>
                            {route.carrier_id ? (
                                <GridItemRow>
                                    <Body2
                                        css={css`
                                            width: 75px;
                                        `}
                                    >
                                        Driver:{' '}
                                    </Body2>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        select
                                        menuItems={partnerDrivers?.map((driver) => ({
                                            key: driver.teammate_id,
                                            value: driver.username,
                                        }))}
                                        onChange={handlePartnerDriverSelect}
                                        value={route?.driver_id || 'NONE'}
                                        disabled={isLocked}
                                    >
                                        <MenuItem value={'NONE'}>
                                            {route.driver_id ? 'Remove driver' : 'Select a driver ...'}
                                        </MenuItem>
                                        {(partnerDrivers || []).map((driver) => (
                                            <MenuItem key={driver.teammate_id} value={driver.teammate_id}>
                                                {driver.username}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </GridItemRow>
                            ) : null}
                        </>
                    ) : null}

                    {resources.length > 0 && (
                        <>
                            {isLocked ? (
                                <StopMetadata label="Resource" value={resource?.name} defaultValue="N/A" />
                            ) : (
                                <GridItemRow>
                                    <Body2>Resource: </Body2>
                                    <TextField
                                        css={css`
                                            width: 100%;
                                            margin-left: 1rem;
                                            .MuiSelect-selectMenu {
                                                padding: 9px;
                                            }
                                        `}
                                        select
                                        variant="outlined"
                                        value={selectedResourceId}
                                        onChange={(e) => handleChangeResource(e.target.value)}
                                        disabled={route?.shell}
                                    >
                                        <MenuItem key={'null-resource'} value={''}>
                                            None
                                        </MenuItem>
                                        {resourcesForToday?.map((option) => (
                                            <MenuItem key={option.resource_id} value={option.resource_id}>
                                                {option.name}{' '}
                                                {option.capacity_cubes ? (
                                                    <span style={{ fontSize: 12 }}>
                                                        ({option.capacity_cubes} cubes)
                                                    </span>
                                                ) : null}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </GridItemRow>
                            )}
                        </>
                    )}

                    <GridItemRow
                        css={css`
                            .MuiFormControlLabel-root {
                                margin-left: 0;
                            }
                        `}
                    >
                        <OnwardSwitch
                            label={<Body2>Require Staging Verification</Body2>}
                            labelPlacement="start"
                            checked={!!route?.needs_verification}
                            onChange={(e) => updateRoute({ route: { ...route, needs_verification: e.target.checked } })}
                        />
                    </GridItemRow>
                </PlanningListItem>
            </Popper>
        </>
    );
}

export default PlanningStops;
